<!--
 * @Author: Tahiti
 * @Date: 2025-12-11
 * @Description: 头像编辑组件 - 使用原生方法实现圆形裁剪
 * @FilePath: /weryai-web/components/ui/AvatarEditor.vue
-->
<template>
    <div
        v-if="visible"
        class="fixed inset-0 z-[4000] flex items-center justify-center bg-black/70"
        @click="handleCancel"
    >
        <div
            ref="modalContainer"
            class="relative bg-[#22242D] rounded-[12px] p-[30px] w-[80%] md:w-[500px] min-h-[400px] max-w-[500px] mx-4 shadow-2xl"
            @click.stop
        >
            <div class="flex justify-between items-center mb-5">
                <p
                    class="text-white text-left text-base md:text-[20px] font-medium"
                >
                    {{ t('profiles.editAvatar') }}
                </p>
                <button
                    @click="handleCancel"
                    class="w-6 h-6 flex items-center justify-center text-white hover:rotate-180 transition-all duration-300"
                >
                    <img
                        src="@/assets/images/common/_close.webp"
                        alt="close"
                        class="w-[24px]"
                    />
                </button>
            </div>

            <!-- 图片编辑区域 -->
            <div
                ref="editorContainer"
                class="relative w-full h-[400px] bg-[#1a1a1a] rounded-[10px] overflow-hidden cursor-move"
                @mousedown="handleMouseDown"
                @mousemove="handleMouseMove"
                @mouseup="handleMouseUp"
                @mouseleave="handleMouseUp"
                @wheel.prevent="handleWheel"
            >
                <!-- 图片容器 -->
                <div
                    ref="imageContainer"
                    class="absolute inset-0 flex items-center justify-center"
                    :style="imageStyle"
                >
                    <img
                        ref="imageElement"
                        :src="computedImageSrc"
                        alt="avatar"
                        class="max-w-none select-none"
                        draggable="false"
                        @load="handleImageLoad"
                        @error="handleImageError"
                    />
                </div>

                <!-- 圆形裁剪框和蒙版 -->
                <canvas
                    ref="maskCanvas"
                    class="absolute inset-0 pointer-events-none"
                    :width="containerWidth"
                    :height="containerHeight"
                ></canvas>
            </div>

            <!-- 操作按钮 -->
            <div class="flex justify-end gap-5 mt-[18px]">
                <button
                    @click="handleCancel"
                    class="min-w-[104px] rounded-[8px] h-[44px] border border-white/20 text-white hover:border-white/30 transition-all duration-300"
                >
                    {{ t('profiles.cancel') }}
                </button>
                <button
                    @click="handleConfirm"
                    class="min-w-[104px] rounded-[8px] h-[44px] bg-create-btn text-black hover:bg-create-btn/80 transition-all duration-300"
                >
                    {{ t('profiles.confirm') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useI18n } from '~/composables/useI18n'

const { t } = useI18n()

// Props
const props = defineProps<{
    visible: boolean
    imageSrc: string | File | null
}>()

// Emits
const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void
    (e: 'confirm', imageData: string): void
    (e: 'cancel'): void
}>()

// Refs
const modalContainer = ref<HTMLDivElement | null>(null)
const editorContainer = ref<HTMLDivElement | null>(null)
const imageContainer = ref<HTMLDivElement | null>(null)
const imageElement = ref<HTMLImageElement | null>(null)
const maskCanvas = ref<HTMLCanvasElement | null>(null)

// 状态
const containerWidth = ref(440)
const containerHeight = ref(400)
const imageScale = ref(1) // 图片缩放比例
const imageX = ref(0) // 图片X偏移
const imageY = ref(0) // 图片Y偏移
const isDragging = ref(false)
const dragStartX = ref(0)
const dragStartY = ref(0)
const dragStartImageX = ref(0)
const dragStartImageY = ref(0)

// 计算属性
const imageStyle = computed(() => {
    return {
        transform: `translate(${imageX.value}px, ${imageY.value}px) scale(${imageScale.value})`,
        transformOrigin: 'center center',
    }
})

// 计算图片源（处理 File 对象）
const computedImageSrc = computed(() => {
    if (!props.imageSrc) return ''
    if (typeof props.imageSrc === 'string') {
        return props.imageSrc
    }
    // File 对象转换为 URL
    return URL.createObjectURL(props.imageSrc)
})

// 圆形裁剪框的半径（固定为200*200，半径为100）
const cropRadius = computed(() => {
    return 100 // 固定半径为100，对应200*200的裁剪框
})

// 监听 visible 变化，重置状态
watch(
    () => props.visible,
    newVal => {
        if (newVal) {
            // 先更新容器尺寸，确保在移动端也能获取正确的宽度
            nextTick(() => {
                updateContainerSize()
                resetImagePosition()
            })
        }
    }
)

// 监听图片源变化
watch(
    () => props.imageSrc,
    () => {
        if (props.visible) {
            nextTick(() => {
                resetImagePosition()
                drawMask()
            })
        }
    }
)

// 图片加载完成
const handleImageLoad = () => {
    nextTick(() => {
        updateContainerSize()
        resetImagePosition()
    })
}

// 图片加载错误
const handleImageError = () => {
    console.error('Failed to load image')
}

// 重置图片位置和缩放
const resetImagePosition = () => {
    if (!imageElement.value || !editorContainer.value) return

    const img = imageElement.value
    const container = editorContainer.value

    // 计算初始缩放，使图片能够覆盖裁剪区域
    const cropSize = cropRadius.value * 2
    const scaleX = cropSize / img.naturalWidth
    const scaleY = cropSize / img.naturalHeight
    imageScale.value = Math.max(scaleX, scaleY) * 1.2 // 稍微放大一点，方便调整

    // 重置位置到中心
    imageX.value = 0
    imageY.value = 0

    // 应用边界限制，确保初始位置也是有效的
    nextTick(() => {
        const constrained = constrainImagePosition(imageX.value, imageY.value)
        imageX.value = constrained.x
        imageY.value = constrained.y
    })
}

// 绘制蒙版（圆形裁剪框外的半透明区域）
const drawMask = () => {
    if (!maskCanvas.value) return

    const canvas = maskCanvas.value
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // 更新画布尺寸
    canvas.width = containerWidth.value
    canvas.height = containerHeight.value

    // 清空画布
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // 绘制半透明黑色背景
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // 使用 composite 操作，在圆形区域内清除蒙版
    ctx.globalCompositeOperation = 'destination-out'
    ctx.beginPath()
    ctx.arc(
        canvas.width / 2,
        canvas.height / 2,
        cropRadius.value,
        0,
        Math.PI * 2
    )
    ctx.fill()

    // 绘制圆形边框
    ctx.globalCompositeOperation = 'source-over'
    ctx.strokeStyle = '#ffffff'
    ctx.lineWidth = 2
    ctx.setLineDash([5, 5]) // 虚线
    ctx.beginPath()
    ctx.arc(
        canvas.width / 2,
        canvas.height / 2,
        cropRadius.value,
        0,
        Math.PI * 2
    )
    ctx.stroke()
}

// 鼠标按下
const handleMouseDown = (e: MouseEvent) => {
    isDragging.value = true
    dragStartX.value = e.clientX
    dragStartY.value = e.clientY
    dragStartImageX.value = imageX.value
    dragStartImageY.value = imageY.value
}

// 计算图片移动的边界限制
// 确保圆形裁剪区域始终在图片范围内
const constrainImagePosition = (x: number, y: number) => {
    if (!imageElement.value || !editorContainer.value) {
        return { x, y }
    }

    const img = imageElement.value

    // 图片在容器中的实际尺寸（考虑缩放）
    const scaledWidth = img.naturalWidth * imageScale.value
    const scaledHeight = img.naturalHeight * imageScale.value

    // 计算图片中心可以移动的范围
    // 约束条件：圆形裁剪区域（中心在容器中心，半径为cropRadius）必须完全在图片内

    // X轴限制：
    // 圆的左边界 >= 图片左边界：containerCenterX - cropRadius >= containerCenterX + x - scaledWidth/2
    // 即：x <= scaledWidth/2 - cropRadius
    // 圆的右边界 <= 图片右边界：containerCenterX + cropRadius <= containerCenterX + x + scaledWidth/2
    // 即：x >= cropRadius - scaledWidth/2
    const minX = cropRadius.value - scaledWidth / 2
    const maxX = scaledWidth / 2 - cropRadius.value

    // Y轴限制：
    // 圆的上边界 >= 图片上边界：containerCenterY - cropRadius >= containerCenterY + y - scaledHeight/2
    // 即：y <= scaledHeight/2 - cropRadius
    // 圆的下边界 <= 图片下边界：containerCenterY + cropRadius <= containerCenterY + y + scaledHeight/2
    // 即：y >= cropRadius - scaledHeight/2
    const minY = cropRadius.value - scaledHeight / 2
    const maxY = scaledHeight / 2 - cropRadius.value

    // 应用限制
    const constrainedX = Math.max(minX, Math.min(maxX, x))
    const constrainedY = Math.max(minY, Math.min(maxY, y))

    return { x: constrainedX, y: constrainedY }
}

// 鼠标移动
const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging.value) return

    const deltaX = e.clientX - dragStartX.value
    const deltaY = e.clientY - dragStartY.value

    const newX = dragStartImageX.value + deltaX
    const newY = dragStartImageY.value + deltaY

    // 应用边界限制
    const constrained = constrainImagePosition(newX, newY)
    imageX.value = constrained.x
    imageY.value = constrained.y
}

// 鼠标释放
const handleMouseUp = () => {
    isDragging.value = false
}

// 鼠标滚轮缩放
const handleWheel = (e: WheelEvent) => {
    e.preventDefault()

    // 使用更平滑的缩放，基于当前缩放值的百分比
    const zoomFactor = 0.1 // 每次缩放10%
    const delta = e.deltaY > 0 ? -zoomFactor : zoomFactor

    // 计算新的缩放值（基于当前值的百分比变化）
    const scaleChange = imageScale.value * delta
    const newScale = Math.max(0.3, Math.min(5, imageScale.value + scaleChange))

    // 确保缩放值确实改变了
    if (Math.abs(newScale - imageScale.value) > 0.01) {
        imageScale.value = newScale
        // 缩放后应用边界限制，确保裁剪区域仍在图片范围内
        nextTick(() => {
            const constrained = constrainImagePosition(
                imageX.value,
                imageY.value
            )
            imageX.value = constrained.x
            imageY.value = constrained.y
        })
    }
}

// 取消
const handleCancel = () => {
    emit('update:visible', false)
    emit('cancel')
}

// 确认 - 裁剪图片并返回
const handleConfirm = () => {
    if (!imageElement.value || !maskCanvas.value) return

    const img = imageElement.value
    if (!img.complete || img.naturalWidth === 0) {
        console.error('Image not loaded')
        return
    }

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // 设置画布尺寸为裁剪后的圆形直径（固定为200*200）
    const outputSize = 200
    canvas.width = outputSize
    canvas.height = outputSize

    // 计算图片在容器中的实际位置和尺寸
    const containerCenterX = containerWidth.value / 2
    const containerCenterY = containerHeight.value / 2

    // 图片在容器中的实际尺寸（考虑缩放）
    const scaledWidth = img.naturalWidth * imageScale.value
    const scaledHeight = img.naturalHeight * imageScale.value

    // 图片中心在容器中的位置
    const imgCenterX = containerCenterX + imageX.value
    const imgCenterY = containerCenterY + imageY.value

    // 裁剪区域中心（就是容器中心）
    const cropCenterX = containerCenterX
    const cropCenterY = containerCenterY

    // 计算裁剪区域相对于图片中心的位置（在原始图片坐标系中）
    const offsetX = (cropCenterX - imgCenterX) / imageScale.value
    const offsetY = (cropCenterY - imgCenterY) / imageScale.value

    // 裁剪区域在原始图片中的位置和尺寸
    const sourceSize = (cropRadius.value * 2) / imageScale.value
    const sourceX = img.naturalWidth / 2 + offsetX - sourceSize / 2
    const sourceY = img.naturalHeight / 2 + offsetY - sourceSize / 2

    // 确保裁剪区域在图片范围内
    const clampedSourceX = Math.max(
        0,
        Math.min(img.naturalWidth - sourceSize, sourceX)
    )
    const clampedSourceY = Math.max(
        0,
        Math.min(img.naturalHeight - sourceSize, sourceY)
    )
    const clampedSourceSize = Math.min(
        sourceSize,
        img.naturalWidth - clampedSourceX,
        img.naturalHeight - clampedSourceY
    )

    // 在主画布上绘制圆形裁剪
    ctx.save()
    ctx.beginPath()
    ctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2)
    ctx.clip()

    // 绘制裁剪后的图片
    ctx.drawImage(
        img as CanvasImageSource,
        clampedSourceX,
        clampedSourceY,
        clampedSourceSize,
        clampedSourceSize,
        0,
        0,
        outputSize,
        outputSize
    )

    ctx.restore()

    // 转换为 base64
    const imageData = canvas.toDataURL('image/png', 1.0)
    emit('confirm', imageData)
    emit('update:visible', false)
}

// 更新容器尺寸
const updateContainerSize = () => {
    if (editorContainer.value) {
        // 使用requestAnimationFrame确保DOM完全渲染后再获取尺寸（特别是移动端）
        requestAnimationFrame(() => {
            if (editorContainer.value) {
                // 直接使用editorContainer的实际尺寸
                const editorRect = editorContainer.value.getBoundingClientRect()
                containerWidth.value = editorRect.width
                containerHeight.value = editorRect.height
                nextTick(() => {
                    drawMask()
                })
            }
        })
    }
}

// 清理对象 URL
const cleanup = () => {
    if (
        computedImageSrc.value &&
        typeof props.imageSrc === 'object' &&
        props.imageSrc instanceof File
    ) {
        URL.revokeObjectURL(computedImageSrc.value)
    }
}

// 生命周期
onMounted(() => {
    updateContainerSize()
    window.addEventListener('resize', updateContainerSize)
})

onUnmounted(() => {
    window.removeEventListener('resize', updateContainerSize)
    cleanup()
})

// 监听图片源变化，清理旧的 URL
watch(
    () => props.imageSrc,
    (newVal, oldVal) => {
        if (oldVal && typeof oldVal === 'object' && oldVal instanceof File) {
            const oldUrl = URL.createObjectURL(oldVal)
            URL.revokeObjectURL(oldUrl)
        }
    }
)
</script>

<style scoped lang="less">
// 样式已通过 Tailwind CSS 类实现
</style>
